import { logError } from '@zg-rentals/log-error';
import ErrorMessageConstants from '../constants/ErrorMessageConstants';

interface LocationRestrictionProps {
  duplicateExists: boolean;
  duplicateSyndicatedUrl: string | null;
  forSaleExists: boolean;
  forSaleUrlOnZillow: string | null;
  isTopListingOwner: boolean;
  paidListingExists: boolean;
  paidMultifamilyLocation: boolean;
  restrictedFromNyc: boolean;
  setPropertyAlreadyPostedWarningMessage?: (data: string | Array<JSX.Element | string>) => void;
}

const getLocationRestrictionErrorMessage = (
  {
    duplicateExists,
    duplicateSyndicatedUrl,
    forSaleExists,
    forSaleUrlOnZillow,
    isTopListingOwner,
    paidListingExists,
    paidMultifamilyLocation,
    restrictedFromNyc,
  }: LocationRestrictionProps,
  isMultifamily = false,
  isMultifamilyCustomer = false,
  hasActiveMultifamilyContract = false,
  isSingleFamilyEnterpriseCustomer = false,
  isPostingPathAdmin = false,
  isHousingConnector = false,
  canPostForSaleListing = false,
  setPropertyAlreadyPostedWarningMessage = (data: string | Array<JSX.Element | string>) => {},
) => {
  const learnMoreElement = (
    <a
      href="https://help.zillowrentalmanager.com/hc/en-us/articles/217482388-Why-didn-t-my-listing-go-live-"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more
    </a>
  );
  const propertyAlreadyPostedElements = (href: string) => [
    'This property is ',
    <a href={href} target="_blank" rel="noopener noreferrer">
      already posted
    </a>,
  ];
  const logErrorAndReturn = (message: Array<string | JSX.Element> | string) => {
    logError({
      error: new Error(typeof message === 'string' ? message : 'handleLocationRestrictionResponse error'),
      errorType: 'LocationRestrictionError',
      context: 'handleLocationRestrictionResponse',
    });
    return message;
  };

  setPropertyAlreadyPostedWarningMessage('');

  if (restrictedFromNyc) {
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.NYC;
    return logErrorAndReturn(message);
  }

  if (!isPostingPathAdmin && !isHousingConnector && paidMultifamilyLocation && !hasActiveMultifamilyContract) {
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED;
    return logErrorAndReturn(message);
  }

  if (paidListingExists && !isTopListingOwner) {
    return logErrorAndReturn(
      ErrorMessageConstants.PAID_LISTING_DUPE_ERROR(
        isMultifamily && isMultifamilyCustomer,
        isSingleFamilyEnterpriseCustomer,
      ),
    );
  }

  if (forSaleExists && !isMultifamily) {
    if (canPostForSaleListing) {
      const postForSaleListingLearnMore = (
        <a
          href="https://help.zillowrentalmanager.com/hc/en-us/articles/213339187-Advertising-For-Sale-and-For-Rent-Listings-Simultaneously"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </a>
      );
      const message = forSaleUrlOnZillow
        ? [
            ...propertyAlreadyPostedElements(forSaleUrlOnZillow),
            ' for sale. You can post it for rent at the same time, but it may receive less visibility. ',
            postForSaleListingLearnMore,
          ]
        : [
            // eslint-disable-next-line max-len
            'This property was already posted for sale elsewhere. You can post it for rent at the same time, but it may receive less visibility. ',
            postForSaleListingLearnMore,
          ];
      return setPropertyAlreadyPostedWarningMessage(message);
    }
    const message = forSaleUrlOnZillow
      ? [
          ...propertyAlreadyPostedElements(forSaleUrlOnZillow),
          ' for sale. Listings cannot be listed for rent and for sale at the same time. ',
          learnMoreElement,
        ]
      : [
          // eslint-disable-next-line max-len
          'This property was already posted for sale elsewhere. Listings cannot be listed for rent and for sale at the same time. ',
          learnMoreElement,
        ];
    return logErrorAndReturn(message);
  }

  if (duplicateExists) {
    if (duplicateSyndicatedUrl) {
      const message = [
        ...propertyAlreadyPostedElements(duplicateSyndicatedUrl),
        ' from another source. ',
        learnMoreElement,
      ];
      return setPropertyAlreadyPostedWarningMessage(message);
    }
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.DUPLICATE.MESSAGE;
    return setPropertyAlreadyPostedWarningMessage(message);
  }

  return null;
};

export default getLocationRestrictionErrorMessage;
